import { notifications } from "@mantine/notifications";
import { ProductAdminRequestDTO } from "Interface/Admin/Products/admin-product.interface";
import React, { useState } from "react";
import { useGetConfigurationByPlantQuery } from "Services/Admin/ConfigurationApiSlice";
import { useAppSelector } from "State/Hooks";
import { BaseUOMSection } from "./FormSections/BaseUOMSection";
import { BasicInfoSection } from "./FormSections/BasicInfoSection";
import { CategoriesSection } from "./FormSections/CategoriesSection";
import { CustomAttributesSection } from "./FormSections/CustomAttributesSection";
import { GarmentsSection } from "./FormSections/GarmentsSection";
import { InventorySection } from "./FormSections/InventorySection";
import { ItemGroupSection } from "./FormSections/ItemGroupSection";
import { PurchaseSection } from "./FormSections/PurchaseSection";
import { SalesSection } from "./FormSections/SalesSection";
import { StatusSection } from "./FormSections/StatusSection";
import { TaxSection } from "./FormSections/TaxSection";

interface ProductFormProps {
  initialValues: ProductAdminRequestDTO;
  onSubmit: (
    data: ProductAdminRequestDTO,
    mainImageFile: File | null,
    additionalImageFiles: (File | null)[],
    addImgsLineNo: number[],
  ) => void;
  onCancel: () => void;
  mode: "add" | "edit" | "view";
  existingMainImage?: string;
  existingAdditionalImages?: string[];
  onDeleteMainImage?: () => Promise<void>;
  onDeleteAdditionalImage?: (index: number) => Promise<void>;
}

const initialFormValues: ProductAdminRequestDTO = {
  productCode: "",
  productName: "",
  title: "",
    remark1: "",
  productDescription: [""],
  category: "",
  subCategory: "",
  brand: "",
  subClassId: "",
  author: "",
  isbn: "",
  isbnPrice: null,
  language: "",
  academic: "",
  merchandise: "",
  baseUom: "pcs",
  model: "",
  purchaseUom: "pcs",
  cost: 0,
  salesUom: "pcs",
  mrp: 0,
  sellingPrice: 0,
  minimumSellingPrice: 0,
  inventoryUom: "pcs",
  minimumStkQty: 0,
  maximumStkQty: 0,
  isActive: "Y",
  isNewArrival: 0,
  isTopSelling: 0,
  remarkTwo: "",
  productGarmentType: {
    id: 0,
    size: "",
    fabric: "",
    collar: "",
    color: "",
    sleeve: "",
    occasion: "",
    pattern: "",
  },
  gst: 0,
  cgst: 0,
  sgst: 0,
  cess: 0,
  hsCode: "",
  minimumInvQty: 0,
  labelPara1: "",
  labelPara2: "",
  labelPara3: "",
  labelPara4: "",
  labelPara5: "",
  labelPara6: "",
  labelPara7: "",
  labelPara8: "",
  parameter1: "",
  parameter2: "",
  parameter3: "",
  parameter4: "",
  parameter5: "",
  parameter6: "",
  parameter7: "",
  parameter8: "",
  detailDescription: "",
  itemGroupId: undefined,
  formatType: undefined,
  itemCondition: undefined,
  productType: "BOOK",
};

const PLANT = process.env.REACT_APP_PLANT || "";

export const ProductForm: React.FC<ProductFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  mode,
  existingMainImage,
  existingAdditionalImages,
  onDeleteMainImage,
  onDeleteAdditionalImage,
}) => {
  const { data: ecomConfig } = useGetConfigurationByPlantQuery(PLANT);
  const config = useAppSelector((state: any) => state.configuration.config);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ProductAdminRequestDTO>({
    ...initialFormValues,
    ...initialValues,
  });
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [addImageFiles, setAddImageFiles] = useState<(File | null)[]>(
    existingAdditionalImages ? Array(5).fill(null) : [],
  );

  const [addImgsLineNo, setAddImgsLineNo] = useState<number[]>(
    existingAdditionalImages
      ? existingAdditionalImages.map((_, index) => index)
      : [],
  );
  const [openSections, setOpenSections] = useState({
    basicInfo: true,
    categories: true,
    status: true,
    taxInfo: true,
    baseUOM: true,
    purchase: true,
    sales: true,
    inventory: true,
    garments: true,
    customAttributes: true,
    itemGroup: true,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const numberFields = [
    "cost",
    "mrp",
    "sellingPrice",
    "minimumSellingPrice",
    "minimumStkQty",
    "maximumStkQty",
    "gst",
    "cgst",
    "sgst",
    "cess",
    "minimumInvQty",
    "isNewArrival",
    "isTopSelling",
    "isbnPrice",
  ];

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleGarmentChange = (field: string, value: string) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     productgarmenttype: {
  //       ...prev.productgarmenttype,
  //       [field]: value
  //     }
  //   }));
  // };

  const validatePrices = (): boolean => {
    if (Number(formData.sellingPrice) > Number(formData.mrp)) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePrices()) return;
  if (isSubmitting) return;
    const submitData: ProductAdminRequestDTO = {
      ...formData,
      isbn: formData.isbn?.trim() || "",
      cost: Number(formData.cost),
      mrp: Number(formData.mrp),
      sellingPrice: Number(formData.sellingPrice),
      minimumSellingPrice: Number(formData.minimumSellingPrice),
      minimumStkQty: Number(formData.minimumStkQty),
      maximumStkQty: Number(formData.maximumStkQty),
      gst: Number(formData.gst),
      cgst: Number(formData.cgst),
      sgst: Number(formData.sgst),
      cess: Number(formData.cess),
      minimumInvQty: Number(formData.minimumInvQty),
      isNewArrival: Number(formData.isNewArrival),
      isTopSelling: Number(formData.isTopSelling),
      isbnPrice: formData.isbnPrice !== null && formData.isbnPrice !== undefined
        ? Number(formData.isbnPrice)
        : null,
    };

    try {
      setIsSubmitting(true);
      
      await onSubmit(submitData, mainImageFile, addImageFiles, addImgsLineNo);
    } catch (error) {

      setIsSubmitting(false);
      console.error("Submission failed:", error);
    }
  };
  return (
    <form onSubmit={handleSubmit} className="h-full">
      <div className="font-body tracking-wider bg-white rounded-md border shadow-sm mb-12 flex flex-col h-[65vh] relative">
        <div className="px-6 py-12 space-y-6 flex-1 overflow-y-auto">
        <BasicInfoSection
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          open={openSections.basicInfo}
          toggleSection={() => toggleSection("basicInfo")}
          mainImageFile={mainImageFile}
          setMainImageFile={setMainImageFile}
          addImageFiles={addImageFiles}
          setAddImageFiles={setAddImageFiles}
          addImgsLineNo={addImgsLineNo}
          setAddImgsLineNo={setAddImgsLineNo}
          existingMainImage={existingMainImage}
          existingAdditionalImages={existingAdditionalImages}
          onDeleteMainImage={onDeleteMainImage}
          onDeleteAdditionalImage={onDeleteAdditionalImage}
        />

        <CategoriesSection
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          open={openSections.categories}
          toggleSection={() => toggleSection("categories")}
        />

        <StatusSection
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          open={openSections.status}
          toggleSection={() => toggleSection("status")}
          industryType={config?.industryType}
        />

        <BaseUOMSection
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          open={openSections.baseUOM}
          toggleSection={() => toggleSection("baseUOM")}
        />

        <PurchaseSection
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          open={openSections.purchase}
          toggleSection={() => toggleSection("purchase")}
        />

        <SalesSection
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          open={openSections.sales}
          toggleSection={() => toggleSection("sales")}
        />

        <InventorySection
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          open={openSections.inventory}
          toggleSection={() => toggleSection("inventory")}
        />

        <TaxSection
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          open={openSections.taxInfo}
          toggleSection={() => toggleSection("taxInfo")}
        />

        {process.env.REACT_APP_STORE_TYPE !== "book" && (
          <GarmentsSection
            formData={formData}
            setFormData={setFormData}
            mode={mode}
            open={openSections.garments}
            toggleSection={() => toggleSection("garments")}
          />
        )}

        {process.env.REACT_APP_STORE_TYPE !== "book" && (
          <CustomAttributesSection
            formData={formData}
            setFormData={setFormData}
            mode={mode}
            open={openSections.customAttributes}
            toggleSection={() => toggleSection("customAttributes")}
          />
        )}

        <ItemGroupSection
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          open={openSections.itemGroup}
          toggleSection={() => toggleSection("itemGroup")}
        />
      </div>

      {mode !== "view" && (
  <div className="bg-white border-t px-6 py-4 rounded-b-md shrink-0">
    <div className="flex justify-end gap-3">
      <button
        type="button"
        disabled={isSubmitting}
        onClick={onCancel}
        className={`px-6 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white text-blue-600 hover:bg-gray-50 ${
          isSubmitting ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        Cancel
      </button>

      <button
        type="submit"
        disabled={isSubmitting}
        className={`flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md transition-all ${
          isSubmitting ? "bg-blue-400 cursor-not-allowed" : "hover:bg-blue-700"
        }`}
      >
        {isSubmitting ? (
          <>
            {/* Simple CSS Loader */}
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            {mode === "add" ? "Creating..." : "Saving..."}
          </>
        ) : (
          mode === "add" ? "Create Product" : "Save Changes"
        )}
      </button>
    </div>
  </div>
)}

      </div>
    </form>
  );
};
